// The sheet's start cursor (Oct 2026, user: "tap to drop the cursor/playback
// head and go from there"): a tap on a bar sets the bar Play starts from, and
// while the band plays it takes the band there. A tap names a WRITTEN bar,
// while the band plays the PLAYED form, where one written bar can sound
// several times (a repeat, the head and every solo chorus). These two
// functions decide which of those times a tap means.

/** The written bar behind a sheet's bar id: the unrolled sheet gives each
 * pass of a bar its own id (`id#n`, chart.ts unrollChart). */
export function writtenMeasureId(id: string): string {
  const hash = id.indexOf('#');
  return hash < 0 ? id : id.slice(0, hash);
}

/** Where the band is: the played bar under way and the chorus it belongs to
 * (played bar indices, end exclusive). */
export interface PlayingAt {
  index: number;
  passStart: number;
  passEnd: number;
}

/**
 * The played bar a tap on a written bar means, or -1 when the band never plays
 * it. Idle (no `at`), the first time it is played. Playing, the time it is
 * played in the same time through as the playhead: a repeat, an ending or a
 * D.S. jumps BACK in the written order, so each run of rising written bars is
 * one time through, and on bar 7 of a repeat's first time a tap on bar 3 means
 * this time's bar 3 while on bar 2 of the second time a tap on bar 4 means the
 * second time's. A bar this time through does not play (the other ending, an
 * intro) answers its time nearest the playhead, a tie going back. Only times
 * inside the chorus under way count, when it plays the bar at all.
 */
export function playedOccurrence(
  playedWritten: readonly (number | undefined)[],
  written: number,
  at?: PlayingAt | null,
): number {
  const times: number[] = [];
  playedWritten.forEach((w, index) => {
    if (w === written) times.push(index);
  });
  if (times.length === 0) return -1;
  if (!at || at.index < 0) return times[0];
  const inChorus = times.filter((index) => index >= at.passStart && index < at.passEnd);
  const candidates = inChorus.length > 0 ? inChorus : times;
  const run = timesThrough(playedWritten);
  const same = candidates.find((index) => run[index] === run[at.index]);
  if (same !== undefined) return same;
  let best = candidates[0];
  for (const index of candidates) {
    if (Math.abs(index - at.index) < Math.abs(best - at.index)) best = index;
  }
  return best;
}

/** Each played bar's time through: a new one starts wherever the written
 * order goes back (a bar the tune does not write continues the one before). */
function timesThrough(playedWritten: readonly (number | undefined)[]): number[] {
  const run: number[] = [];
  let current = 0;
  let last: number | undefined;
  playedWritten.forEach((w, index) => {
    if (w !== undefined) {
      if (last !== undefined && w <= last) current += 1;
      last = w;
    }
    run[index] = current;
  });
  return run;
}
